<template lang="pug">
.profile-achievements-tab
  p.section-label COLLECTIONS

  .collections-loading(v-if="loading && !collections.length")
    el-icon(:size="28" class="loading-spinner")
      Loading
    p Loading collections...

  template(v-else-if="collections.length")
    .halo-row
      button.halo-item(
        v-for="collection in collections"
        :key="collection.id"
        type="button"
        @click="toggleExpand(collection.id)"
      )
        .halo-wrap(:class="{ expanded: expandedCollectionId === collection.id }")
          collection-halo-icon(
            :cover-image-url="collection.coverImageUrl"
            :owner-avatar="collection.ownerAvatar"
            :title="collection.title"
            :collected-count="completionProgressCount(collection)"
            :total-stickers="requiredStickerCount(collection)"
            :is-completed="collection.isCompleted"
            :show-progress="collection.showProgress !== false"
          )
        span.halo-title {{ collection.title }}
        span.archived-badge(v-if="collection.status === 'ARCHIVED'") Archived
        span.halo-subtext(
          v-if="collection.showProgress !== false && !collection.isCompleted"
        ) {{ completionProgressLabel(collection) }}
        span.halo-subtext.is-complete(
          v-else-if="collection.showProgress !== false && collection.isCompleted"
        ) Complete

    profile-collection-expanded-panel(
      v-if="expandedCollection"
      ref="panelRef"
      :collection="expandedCollection"
      @close="expandedCollectionId = null"
    )

  .empty-state(v-else)
    p Collect stickers from a collection to see progress here.
    router-link.empty-link(to="/web/dashboard") Browse marketplace

  .achievements-footer
    p.footer-label More coming soon
</template>

<script lang="ts">
import {
  computed, defineComponent, nextTick, ref, watch,
} from 'vue';
import { Loading } from '@element-plus/icons-vue';
import type { MyStickerCollection } from '@/data/stickerCollection.types';
import CollectionHaloIcon from './CollectionHaloIcon.vue';
import ProfileCollectionExpandedPanel from './ProfileCollectionExpandedPanel.vue';
import {
  completionProgressCount,
  completionProgressLabel,
  requiredStickerCount,
} from '@/utils/stickerCollectionProgress';

export default defineComponent({
  name: 'ProfileAchievementsTab',
  components: {
    CollectionHaloIcon,
    ProfileCollectionExpandedPanel,
    Loading,
  },
  props: {
    collections: {
      type: Array as () => MyStickerCollection[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** Collection to open once loaded, e.g. from the completion push (`?collectionId=`). */
    initialExpandedId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const expandedCollectionId = ref<string | null>(null);
    const panelRef = ref<{ $el?: HTMLElement } | null>(null);

    let openedInitial = false;
    watch(
      () => [props.collections, props.initialExpandedId] as const,
      ([collections, id]) => {
        if (openedInitial || !id) return;
        if (collections.some((c) => c.id === id)) {
          expandedCollectionId.value = id;
          openedInitial = true;
          nextTick(() => panelRef.value?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }));
        }
      },
      { immediate: true },
    );

    const expandedCollection = computed(
      () => props.collections.find((c) => c.id === expandedCollectionId.value) || null,
    );

    function toggleExpand(id: string) {
      expandedCollectionId.value = expandedCollectionId.value === id ? null : id;
    }

    return {
      completionProgressCount,
      completionProgressLabel,
      requiredStickerCount,
      expandedCollectionId,
      panelRef,
      expandedCollection,
      toggleExpand,
    };
  },
});
</script>

<style lang="scss" scoped>
.profile-achievements-tab {
  padding: 0.5rem 0 1rem;
}

.section-label {
  margin: 0 0 1rem;
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9ca3af;
}

.collections-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  min-height: 8rem;
  justify-content: center;
  color: #6b7280;

  .loading-spinner {
    color: #0d9488;
    animation: spin 1.5s linear infinite;
  }
}

.halo-row {
  display: flex;
  gap: 1.25rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  margin-bottom: 0.25rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.halo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  width: 5rem;
  padding: 0;
}

.halo-wrap {
  transition: transform 0.15s ease;

  &.expanded {
    transform: scale(1.1);
  }

  .halo-item:hover & {
    transform: scale(1.05);
  }

  &.expanded:hover {
    transform: scale(1.1);
  }
}

.halo-title {
  font-size: 0.625rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.25;
  color: #1a2744;
  max-width: 5rem;
}

.halo-subtext {
  margin-top: -0.25rem;
  font-size: 0.5625rem;
  font-weight: 600;
  color: #9ca3af;

  &.is-complete {
    color: #0d9488;
    font-weight: 900;
  }
}

.archived-badge {
  display: inline-block;
  padding: 0.0625rem 0.375rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.5625rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;

  p {
    margin: 0 0 1rem;
    color: #9ca3af;
    font-size: 0.875rem;
  }
}

.empty-link {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0d9488;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.achievements-footer {
  margin-top: 1.5rem;
  height: 1px;
  background: #f3f4f6;
}

.footer-label {
  margin: 1.5rem 0 0;
  font-size: 0.625rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #d1d5db;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
